import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AsyncSelect from 'react-select/async'; // Import AsyncSelect for autocomplete
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CreateTicketFormData } from '../../types/ticket';
import FormInput from './form/FormInput';
import FormTextArea from './form/FormTextArea';
import FormSelect from './form/FormSelect';
import FileUpload from '../ui/custom/FileUpload';
import FilePreview from './FilePreview';
import { AlertCircle, Save, Sparkles } from 'lucide-react';
import { useMetadata } from '../../hooks/useMetadata';
import { useTheme } from '../../contexts/ThemeContext';
import { ticketService } from '../../services/ticketService';
import { tagService } from '../../services/tagService';
import { uploadService, UploadProgress } from '../../services/uploadService';
import { createTicketSchema } from '../../schemas/ticketSchemas';
import { groupAgentsService } from '../../services/groupAgentsService';
import { userService } from '../../services/userService';
import { customerService } from '../../services/customerService';
import CustomerSearch from '../customers/CustomerSearch';
import DynamicFormRenderer from '../forms/DynamicFormRenderer';
import { formTemplateService, type FormTemplate } from '../../services/formTemplateService';
import { useToast } from '../../contexts/ToastContext';
import { httpClient } from '../../services/httpClient';
import { MagnifyingGlassIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import {
  tagTitleSuggestionService,
  TagTitleSuggestion
} from '../../services/tagTitleSuggestionService';
import { settingsService } from '../../services/settingsService';
import { StepFormSection } from './StepFormSection';
import { PrioritySelector, type Priority } from '../customs/PrioritySelector';
import { cn } from '../../lib/utils';
import { Switch } from '../ui/custom/Switch';

// Interface for the form template structure
// FormTemplate interface is imported from formTemplateService

interface CreateTicketFormProps {
  onSubmit: (
    data: CreateTicketFormData & { id: number; form_template_id?: number; form_response?: any }
  ) => void;
  onCancel: () => void;
}

const CreateTicketForm = ({ onSubmit, onCancel }: CreateTicketFormProps): JSX.Element => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors }
  } = useForm<CreateTicketFormData>({
    defaultValues: {
      priority: 'medium',
      isPublic: false
    },
    resolver: zodResolver(createTicketSchema),
    mode: 'onChange'
  });

  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [selectedTags, setSelectedTags] = useState<{ label: string; value: string }[]>([]);

  // Handle tag selection changes
  const handleTagChange = (newValue: any) => {
    const typedTags = newValue as { label: string; value: string }[];
    setSelectedTags(typedTags || []);
    setValue(
      'tags',
      (typedTags || []).map((tag) => tag.value)
    );
  };
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [searchPhoneNumber, setSearchPhoneNumber] = useState('');
  const [customerError, setCustomerError] = useState<string | null>(null);
  const [phoneValidationError, setPhoneValidationError] = useState<string | null>(null);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [externalRef, setExternalRef] = useState('');

  // Form template states
  const [activeTab, setActiveTab] = useState<string>('description');
  const [formTemplates, setFormTemplates] = useState<FormTemplate[]>([]);
  const [formTemplatesLoading, setFormTemplatesLoading] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<FormTemplate | null>(null);
  const [formResponse, setFormResponse] = useState<any>({});
  const [editedCustomer, setEditedCustomer] = useState<{ id: number; full_name: string } | null>(
    null
  );
  const [editedPhoneNumber, setEditedPhoneNumber] = useState('');
  const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);
  const customerNameInputRef = useRef<HTMLInputElement>(null);

  // External reference integration states
  const [externalRefConfig, setExternalRefConfig] = useState<any>(null);
  const [isLoadingExternalRef, setIsLoadingExternalRef] = useState(false);

  // Title suggestions states
  const [titleSuggestionsEnabled, setTitleSuggestionsEnabled] = useState(false);
  const [titleSuggestions, setTitleSuggestions] = useState<TagTitleSuggestion[]>([]);
  const [selectedTitleSuggestion, setSelectedTitleSuggestion] = useState<TagTitleSuggestion | null>(
    null
  );
  const [autoTitleEnabled, setAutoTitleEnabled] = useState(false);

  // Toast Context
  const { showToast } = useToast();

  const priority = watch('priority');
  const titleValue = watch('title');

  // Clear selected suggestion when user manually edits the title
  useEffect(() => {
    if (selectedTitleSuggestion && titleValue !== selectedTitleSuggestion.title) {
      setSelectedTitleSuggestion(null);
    }
  }, [titleValue, selectedTitleSuggestion]);

  // Load metadata on component mount
  const { sources, categories, types, groups } = useMetadata();
  const [groupAgents, setGroupAgents] = useState<{ value: string; label: string }[]>([]);

  // Fetch form templates on component mount
  useEffect(() => {
    const fetchFormTemplates = async () => {
      try {
        setFormTemplatesLoading(true);
        const templates = await formTemplateService.getFormTemplates();

        if (Array.isArray(templates)) {
          setFormTemplates(templates);
        }
      } catch (error) {
        console.error('Error fetching form templates:', error);
      } finally {
        setFormTemplatesLoading(false);
      }
    };

    fetchFormTemplates();
  }, []);

  // Load external reference configuration
  useEffect(() => {
    const loadExternalRefConfig = async () => {
      try {
        const response = await httpClient.get('/api/external-reference/config');
        if (response) {
          setExternalRefConfig(response);
        }
      } catch (error) {
        console.error('Error loading external reference config:', error);
      }
    };

    loadExternalRefConfig();
  }, []);

  // Load title suggestions setting
  useEffect(() => {
    const loadTitleSuggestionsSetting = async () => {
      try {
        const enabled = await settingsService.getTicketTitleSuggestionsEnabled();
        setTitleSuggestionsEnabled(enabled);
      } catch (error) {
        console.error('Error loading title suggestions setting:', error);
      }
    };

    loadTitleSuggestionsSetting();
  }, []);

  // Load auto-title setting from API
  useEffect(() => {
    const loadAutoTitleSetting = async () => {
      if (!titleSuggestionsEnabled) return;
      try {
        const enabled = await settingsService.getAutoTitleEnabled();
        setAutoTitleEnabled(enabled);
      } catch (error) {
        console.error('Error loading auto-title setting:', error);
      }
    };

    loadAutoTitleSetting();
  }, [titleSuggestionsEnabled]);

  // Fetch title suggestions when tags change and feature is enabled
  useEffect(() => {
    const fetchTitleSuggestions = async () => {
      if (!titleSuggestionsEnabled || selectedTags.length === 0) {
        setTitleSuggestions([]);
        return;
      }

      try {
        const tagIds = selectedTags.map((tag) => parseInt(tag.value));
        const suggestions = await tagTitleSuggestionService.getTitlesByTagIds(tagIds);
        setTitleSuggestions(suggestions);
      } catch (error) {
        console.error('Error fetching title suggestions:', error);
        setTitleSuggestions([]);
      }
    };

    fetchTitleSuggestions();
  }, [selectedTags, titleSuggestionsEnabled]);

  // Auto-fill title when autoTitleEnabled is true and suggestions are available
  useEffect(() => {
    if (autoTitleEnabled && titleSuggestions.length > 0 && !selectedTitleSuggestion) {
      const firstSuggestion = titleSuggestions[0];
      setValue('title', firstSuggestion.title);
      setSelectedTitleSuggestion(firstSuggestion);
      tagTitleSuggestionService.incrementUsageCount(firstSuggestion.id);
    }
  }, [autoTitleEnabled, titleSuggestions, selectedTitleSuggestion, setValue]);

  // Reset selected suggestion when tags are cleared
  useEffect(() => {
    if (selectedTags.length === 0) {
      setSelectedTitleSuggestion(null);
      // Reset title when tags are cleared
      setValue('title', '');
    }
  }, [selectedTags, setValue]);

  // Handle title selection from suggestions
  const handleTitleSuggestionSelect = (suggestion: TagTitleSuggestion) => {
    setValue('title', suggestion.title);
    setSelectedTitleSuggestion(suggestion);
    // Increment usage count in background
    tagTitleSuggestionService.incrementUsageCount(suggestion.id);
  };

  // File upload is handled by the uploadService

  const handleFormSubmit = async (data: CreateTicketFormData) => {
    if (isSubmitting) return; // Prevent duplicate submissions

    setIsSubmitting(true);

    try {
      // Convert tag values to numbers
      const tagIds = selectedTags.map((tag) => parseInt(tag.value));

      // Create ticket data with Myanmar timezone (UTC+06:30) handling
      const now = new Date();
      const myanmarTimezoneOffset = '+06:30'; // Myanmar timezone

      // Prepare ticket data for API call
      const apiPayload = {
        ...data,
        tags: tagIds,
        source_id: parseInt(data.source),
        category_id: parseInt(data.category),
        type_id: parseInt(data.type),
        group_id: parseInt(data.group),
        assigned_to_id:
          data.assignedTo && data.assignedTo !== 'ANY_AGENT'
            ? parseInt(data.assignedTo)
            : undefined,
        customer_id: data.customerId,
        // Add form template data if available
        form_template_id: selectedTemplate?.id,
        form_response: formResponse,
        // Ensure dates are in Myanmar timezone format
        created_at: now.toISOString().replace('Z', myanmarTimezoneOffset)
      };

      // Create ticket with proper data structure first
      // Add form template data to the API payload if selected
      if (selectedTemplate?.id) {
        apiPayload.form_template_id = selectedTemplate.id;
        apiPayload.form_response = formResponse;
      }

      // Create the ticket using the API payload
      // Use type assertion to handle the API payload type
      const createdTicket = await ticketService.createTicket(apiPayload as any);

      // Now that we have a valid ticket ID, upload files if any
      // We don't need to track attachmentIds separately since they're linked automatically

      if (data.attachments && data.attachments.length > 0) {
        try {
          // Use uploadFiles with the actual ticket ID
          await uploadService.uploadFiles(
            createdTicket.id, // Use the real ticket ID now
            data.attachments,
            (progress: UploadProgress) => {
              setUploadProgress((prev) => ({
                ...prev,
                [progress.fileName]: progress.progress
              }));
            }
          );

          // The attachments are already associated with the ticket during upload
          // No need to update the ticket with attachment IDs as they're already linked
          // The backend handles this automatically when we upload with the correct ticket ID
        } catch (error) {
          console.error(t('tickets.imagePasted'), error);
          // Continue with the ticket creation even if attachment upload fails
          // We already have a valid ticket at this point
        }
      }

      // Call onSubmit with the created ticket
      onSubmit({
        ...data,
        id: createdTicket.id,
        form_template_id: selectedTemplate?.id,
        form_response: formResponse
      });
    } catch (error) {
      console.error('Error creating ticket:', error);
      // Log the error but don't set any state as we removed submitError
    } finally {
      setIsSubmitting(false);
      // Re-enable the submit button
      const submitButton = document.querySelector('button[type="submit"]');
      if (submitButton) {
        submitButton.removeAttribute('disabled');
      }
    }
  };

  // Function to load all tags initially
  const loadInitialTags = async () => {
    try {
      const tags = await tagService.getAllTags();
      const options = tags
        .filter((tag) => tag.is_active) // Only show active tags
        .map((tag) => ({
          label: tag.name,
          value: tag.id.toString()
        }));
      return options;
    } catch (error) {
      console.error('Error loading initial tags:', error);
      return [];
    }
  };

  // Debounced search function
  const debouncedLoadTags = async (
    inputValue: string,
    callback: (options: { label: string; value: string }[]) => void
  ) => {
    if (!inputValue || inputValue.length < 1) {
      callback([]);
      return;
    }

    try {
      const tags = await tagService.searchTags(inputValue);
      // Filter out already selected tags
      const filteredTags = tags.filter(
        (tag) =>
          !selectedTags.some((selected) => selected.value === tag.id.toString()) &&
          tag.name.toLowerCase().includes(inputValue.toLowerCase())
      );

      const options = filteredTags.map((tag) => ({
        label: tag.name,
        value: tag.id.toString()
      }));

      callback(options);
    } catch (error) {
      console.error('Error fetching tags:', error);
      callback([]);
    }
  };

  // Define the timeout property for loadTagOptions
  interface LoadTagOptionsFunction {
    (inputValue: string, callback: (options: { label: string; value: string }[]) => void): void;
    timeout?: NodeJS.Timeout;
  }

  const loadTagOptions: LoadTagOptionsFunction = (
    inputValue: string,
    callback: (options: { label: string; value: string }[]) => void
  ) => {
    if (!inputValue) {
      // Load all tags when no input
      loadInitialTags().then(callback);
      return;
    }

    // Clear the timeout if it exists
    if (loadTagOptions.timeout) {
      clearTimeout(loadTagOptions.timeout);
    }

    // Set a new timeout
    loadTagOptions.timeout = setTimeout(() => {
      debouncedLoadTags(inputValue, callback);
    }, 300); // 300ms debounce time
  };
  // Load agents when group changes
  useEffect(() => {
    const loadGroupAgents = async () => {
      if (!selectedGroup) {
        setGroupAgents([]);
        return;
      }

      try {
        const groupId = parseInt(selectedGroup);
        const agents = await groupAgentsService.getAgentsByGroupId(groupId);
        const agentDetails = await Promise.all(
          agents.map(async (agent) => {
            const user = await userService.getUser(agent.user_id);
            return {
              value: user.id.toString(),
              label: `${user.full_name} (${user.email})`
            };
          })
        );
        setGroupAgents(agentDetails);
      } catch (error) {
        console.error('Error loading group agents:', error);
      }
    };

    loadGroupAgents();
    if (isCustomerModalOpen) {
      setTimeout(() => {
        customerNameInputRef.current?.focus();
      }, 100);
    }
  }, [isCustomerModalOpen, selectedGroup]);

  // Watch for group changes
  useEffect(() => {
    const subscription = watch((value, info) => {
      if (info.name === 'group') {
        setSelectedGroup(value.group || '');
        // Reset assigned agent when group changes
        if (value.group !== selectedGroup) {
          setValue('assignedTo', '');
        }
      }
    });
    return () => subscription.unsubscribe?.();
  }, [watch, setValue, selectedGroup]);

  // Function to call external reference API
  const handleExternalReferenceSearch = async () => {
    if (!externalRefConfig?.enabled || !searchPhoneNumber) return;

    setIsLoadingExternalRef(true);
    try {
      const response = await httpClient.post('/api/external-reference/lookup', {
        phone: searchPhoneNumber
      });

      const data = (response as any).data;

      // Check if customer data was found
      if (data && (data.name || data._id)) {
        // Auto-fill customer name and external reference ID
        if (data.name) {
          setNewCustomerName(data.name);
        }
        if (data._id) {
          setExternalRef(data._id);
        }
        toast.success(t('integrationSettings.externalReference.lookupSuccess'));
      } else {
        // No customer information found
        toast.error(t('integrationSettings.externalReference.noCustomerFound'));
      }
    } catch (error: any) {
      console.error('External reference lookup error:', error);
      if (error.response?.status === 408) {
        toast.error(t('integrationSettings.externalReference.lookupTimeout'));
      } else {
        toast.error(t('integrationSettings.externalReference.lookupError'));
      }
    } finally {
      setIsLoadingExternalRef(false);
    }
  };

  // Function to reset customer fields when opening modal
  const resetCustomerFields = () => {
    setNewCustomerName('');
    setExternalRef('');
    setCustomerError(null);
    setPhoneValidationError(null);
  };

  const handleCreateCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCustomerName || !searchPhoneNumber) return;

    // Validate phone number - support international formats
    // Allow: digits, spaces, hyphens, parentheses, plus sign
    // Minimum 7 digits (some countries have short numbers)
    const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;
    const digitsOnly = searchPhoneNumber.replace(/\D/g, '');

    if (!phoneRegex.test(searchPhoneNumber) || digitsOnly.length < 7 || digitsOnly.length > 15) {
      setPhoneValidationError(
        'Please enter a valid phone number (7-15 digits, may include +, -, spaces, or parentheses)'
      );
      return;
    }

    setPhoneValidationError(null);
    setIsCreatingCustomer(true);
    try {
      const newCustomer = await customerService.createCustomer({
        full_name: newCustomerName,
        phone: searchPhoneNumber,
        external_ref: externalRef || undefined
      });

      setEditedCustomer({ id: newCustomer.id, full_name: newCustomer.full_name });
      setEditedPhoneNumber(searchPhoneNumber);
      setValue('customerId', newCustomer.id);
      setValue('customerName', newCustomer.full_name);
      setValue('phoneNumber', searchPhoneNumber);
      setNewCustomerName('');
      setExternalRef('');
      setCustomerError(null);
      setIsCustomerModalOpen(false);
    } catch (error) {
      console.error('Error creating customer:', error);
      setCustomerError('Error creating customer');
    } finally {
      setIsCreatingCustomer(false);
    }
  };

  // Show loading state while form is initializing
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [formError] = useState<string | null>(null);

  // Use the form loading state in the submit handler
  useEffect(() => {
    // Update form loading state based on form templates loading
    setIsFormLoading(formTemplatesLoading);
  }, [formTemplatesLoading]);

  if (isFormLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div
          className={`animate-spin rounded-full h-8 w-8 border-b-2 ${
            isDark ? 'border-blue-400' : 'border-blue-500'
          }`}
        />
      </div>
    );
  }

  if (formError) {
    return (
      <div
        className={`flex items-center justify-center p-8 ${
          isDark ? 'text-red-400' : 'text-red-500'
        }`}
      >
        <AlertCircle className="h-5 w-5 mr-2" />
        <span>{formError}</span>
      </div>
    );
  }

  return (
    <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-sm`}>
      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}
      >
        {/* Form Content */}
        <div className="">
          {/* Priority Selection */}
          <StepFormSection
            step={1}
            title={t('forms.priorityLevel')}
            description={t('forms.priorityLevelDescription')}
          >
            <PrioritySelector
              value={priority as Priority}
              onChange={(value) => setValue('priority', value, { shouldValidate: true })}
              className={isDark ? 'dark' : ''}
            />
          </StepFormSection>

          {/* Main Form Fields */}
          <div className="">
            <StepFormSection
              step={2}
              title={t('forms.ticketMainInfo')}
              description={t('forms.ticketMainInfoDescription')}
            >
              {/* Tags Autocomplete */}
              <div>
                <label
                  htmlFor="tags"
                  className={`block text-sm font-medium ${
                    isDark ? 'text-gray-200' : 'text-gray-700'
                  }`}
                >
                  {t('pages.createTicket.fields.tags')}
                  <span className="text-red-500 ms-1">*</span>
                </label>
                <AsyncSelect
                  isMulti
                  cacheOptions={false}
                  defaultOptions={true}
                  loadOptions={(inputValue, callback) => {
                    loadTagOptions(inputValue, callback);
                  }}
                  defaultValue={[]}
                  value={selectedTags}
                  onChange={handleTagChange}
                  placeholder={t('pages.createTicket.fields.tagsPlaceholder')}
                  className="mt-1"
                  classNamePrefix="react-select"
                  noOptionsMessage={({ inputValue }) =>
                    !inputValue
                      ? t('pages.createTicket.fields.noTagsAvailable')
                      : inputValue.length < 1
                        ? t('pages.createTicket.fields.typeAtLeastOneChar')
                        : t('pages.createTicket.fields.noMatchingTags')
                  }
                  loadingMessage={() => t('pages.createTicket.fields.loadingTags')}
                  filterOption={null}
                  isClearable={true}
                  isSearchable={true}
                  menuPlacement="auto"
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '42px',
                      backgroundColor: isDark ? '#374151' : 'white',
                      borderColor: isDark ? '#4B5563' : '#E5E7EB',
                      '&:hover': {
                        borderColor: isDark ? '#6B7280' : '#9CA3AF'
                      }
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: isDark ? '#374151' : 'white',
                      border: `1px solid ${isDark ? '#4B5563' : '#E5E7EB'}`
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: isDark
                        ? state.isFocused
                          ? '#4B5563'
                          : '#374151'
                        : state.isFocused
                          ? '#F3F4F6'
                          : 'white',
                      color: isDark ? '#F3F4F6' : '#111827',
                      '&:active': {
                        backgroundColor: isDark ? '#6B7280' : '#E5E7EB'
                      }
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: isDark ? '#1F2937' : '#EFF6FF',
                      borderRadius: '4px'
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: isDark ? '#60A5FA' : '#1D4ED8',
                      padding: '2px 6px'
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: isDark ? '#60A5FA' : '#1D4ED8',
                      ':hover': {
                        backgroundColor: isDark ? '#374151' : '#DBEAFE',
                        color: isDark ? '#93C5FD' : '#1E40AF'
                      }
                    }),
                    input: (base) => ({
                      ...base,
                      color: isDark ? '#F3F4F6' : '#111827'
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: isDark ? '#9CA3AF' : '#6B7280'
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: isDark ? '#F3F4F6' : '#111827'
                    })
                  }}
                />
                {errors.tags && <p className="mt-2 text-sm text-red-500">{errors.tags.message}</p>}
              </div>
              {/* Title field with suggestions */}
              <div className="relative">
                {/* Title suggestions dropdown */}
                {titleSuggestionsEnabled && titleSuggestions.length > 0 && (
                  <div
                    className={cn(
                      'animate-in fade-in-0 slide-in-from-top-2 duration-300 mb-4',
                      'rounded-lg border border-blue-500/15 bg-blue-500/[0.03] p-4'
                    )}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center justify-center size-6 rounded-md bg-blue-500/10">
                          <Sparkles className="size-3.5 text-blue-500" />
                        </div>
                        <span className="text-sm font-medium text-foreground">
                          {t('settings.ticketTitleSuggestions.suggestedTitles')}
                        </span>
                      </div>
                    </div>
                    <select
                      value={selectedTitleSuggestion?.id || ''}
                      onChange={(e) => {
                        const suggestionId = parseInt(e.target.value);
                        const suggestion = titleSuggestions.find((s) => s.id === suggestionId);
                        if (suggestion) {
                          handleTitleSuggestionSelect(suggestion);
                        }
                      }}
                      className={cn(
                        'w-full rounded-md shadow-sm py-2 px-3 border border-border text-sm transition-opacity',
                        isDark
                          ? 'bg-gray-700 border-gray-600 text-gray-100 focus:ring-indigo-500 focus:border-indigo-500'
                          : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                      )}
                    >
                      <option value="">
                        {t('settings.ticketTitleSuggestions.selectFromSuggestions')}
                      </option>
                      {titleSuggestions.map((suggestion) => (
                        <option key={suggestion.id} value={suggestion.id}>
                          {suggestion.title}
                        </option>
                      ))}
                    </select>
                    <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {autoTitleEnabled
                        ? t('settings.ticketTitleSuggestions.autoGenerated')
                        : t('settings.ticketTitleSuggestions.enterManually')}
                    </p>
                  </div>
                )}

                {/* Title input */}
                <div>
                  <label
                    htmlFor="title"
                    className={`block text-sm font-medium ${
                      isDark ? 'text-gray-200' : 'text-gray-700'
                    }`}
                  >
                    {t('pages.createTicket.fields.title')}
                    <span className="text-red-500 ms-1">*</span>
                  </label>
                  <input
                    type="text"
                    id="title"
                    disabled={autoTitleEnabled}
                    placeholder={t('pages.createTicket.fields.titlePlaceholder')}
                    {...register('title')}
                    className={cn(
                      'mt-1 block w-full rounded-md shadow-sm py-2 px-3 border text-sm',
                      errors.title
                        ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                        : 'border-border focus:ring-indigo-500 focus:border-indigo-500',
                      autoTitleEnabled &&
                        'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-700',
                      isDark
                        ? 'bg-gray-700 border-gray-600 text-gray-100'
                        : 'bg-white border-gray-300 text-gray-900'
                    )}
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-500">{errors.title.message}</p>
                  )}
                </div>
              </div>
              {/* Ticket Classification */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormSelect
                  label={t('pages.createTicket.fields.source')}
                  name="source"
                  required
                  options={sources}
                  register={register}
                  error={errors.source?.message}
                />
                <FormSelect
                  label={t('pages.createTicket.fields.category')}
                  name="category"
                  required
                  options={categories}
                  register={register}
                  error={errors.category?.message}
                />
                <FormSelect
                  label={t('pages.createTicket.fields.type')}
                  name="type"
                  required
                  options={types}
                  register={register}
                  error={errors.type?.message}
                />
              </div>
            </StepFormSection>

            <StepFormSection
              step={3}
              title={t('forms.assignmentTitle')}
              description={t('forms.assignmentDescription')}
            >
              {/* Customer Search Section */}
              <CustomerSearch
                onSelectCustomer={(customer) => {
                  setEditedCustomer({ id: customer.id, full_name: customer.full_name });
                  setEditedPhoneNumber(customer.phone || searchPhoneNumber);
                  setValue('customerId', customer.id);
                  setValue('customerName', customer.full_name);
                  setValue('phoneNumber', customer.phone || searchPhoneNumber);
                }}
                selectedCustomer={editedCustomer}
                selectedPhone={editedPhoneNumber}
                onCreateCustomer={(phoneNumber) => {
                  setSearchPhoneNumber(phoneNumber);
                  resetCustomerFields();
                  setIsCustomerModalOpen(true);
                }}
              />
              {/* Assignment */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <FormSelect
                    label={t('pages.createTicket.fields.assignToGroup')}
                    name="group"
                    required
                    options={groups}
                    register={register}
                    error={errors.group?.message}
                  />
                </div>

                <div className={`${!selectedGroup ? 'opacity-50' : ''}`}>
                  <FormSelect
                    label={t('pages.createTicket.fields.assignToAgent')}
                    name="assignedTo"
                    options={[
                      {
                        value: 'ANY_AGENT',
                        label: 'Any agent'
                      },
                      ...groupAgents.map((agent) => ({
                        value: agent.value,
                        label: agent.label
                      }))
                    ]}
                    register={register}
                    error={errors.assignedTo?.message}
                    disabled={!selectedGroup || !groupAgents.length}
                    required
                  />
                </div>
              </div>
            </StepFormSection>

            <StepFormSection
              step={4}
              title={t('forms.ticketDetailTitle')}
              description={t('forms.ticketDetailDescription')}
            >
              <div className="">
                {/* Custom Tabs Navigation */}
                <div className={`flex border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <button
                    type="button"
                    className={`py-2 px-4 text-sm font-medium border-b-2 ${
                      activeTab === 'description'
                        ? isDark
                          ? 'border-blue-500 text-blue-400'
                          : 'border-blue-500 text-blue-600'
                        : isDark
                          ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-700'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                    onClick={() => {
                      setActiveTab('description');
                      setValue('description', ''); // Clear the value when switching back to description
                    }}
                  >
                    {t('pages.createTicket.fields.description')}
                  </button>
                  <button
                    type="button"
                    className={`py-2 px-4 text-sm font-medium border-b-2 ${
                      activeTab === 'form_template'
                        ? isDark
                          ? 'border-blue-500 text-blue-400'
                          : 'border-blue-500 text-blue-600'
                        : isDark
                          ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-700'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                    onClick={() => {
                      setActiveTab('form_template');
                      // If switching to form template tab and a template is selected, make description not required
                      if (selectedTemplate) {
                        setValue('description', ' '); // Set a space to pass validation
                      }
                    }}
                  >
                    {t('pages.createTicket.fields.templates')}
                  </button>
                </div>

                {/* Tab Content */}
                <div className="mt-4">
                  {activeTab === 'description' && (
                    <FormTextArea
                      label="Ticket Description"
                      name="description"
                      required={activeTab === 'description' || !selectedTemplate}
                      placeholder={t('pages.createTicket.fields.descriptionPlaceholder')}
                      register={register}
                      error={errors.description?.message}
                      maxAttachments={5}
                      currentAttachments={watch('attachments')?.length || 0}
                      onImagePaste={(file) => {
                        // Add the pasted image to the attachments
                        const currentAttachments = watch('attachments') || [];
                        setValue('attachments', [...currentAttachments, file]);
                        showToast(t('tickets.imagePasted'), 'success');
                      }}
                    />
                  )}

                  {activeTab === 'form_template' && (
                    <>
                      <div className="mb-4">
                        <select
                          className={`w-full p-2 rounded-md border ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                          onChange={(e) => {
                            const value = parseInt(e.target.value);
                            if (!isNaN(value)) {
                              const template = formTemplates.find((t) => t.id === value);
                              setSelectedTemplate(template || null);
                              // Reset form response when template changes
                              setFormResponse({});

                              // If a template is selected and we're on the form template tab, make description not required
                              if (template && activeTab === 'form_template') {
                                setValue('description', ' '); // Set a space to pass validation
                              } else if (!template && activeTab === 'form_template') {
                                setValue('description', ''); // Clear if no template selected
                              }
                            } else {
                              setSelectedTemplate(null);
                              setFormResponse({});
                              setValue('description', '');
                            }
                          }}
                          disabled={formTemplatesLoading}
                        >
                          <option value="">{t('pages.createTicket.fields.selectTemplate')}</option>
                          {formTemplates.map((template) => (
                            <option key={template.id} value={template.id}>
                              {template.name}
                            </option>
                          ))}
                        </select>
                        {formTemplatesLoading && (
                          <div className="mt-2 flex items-center">
                            <div className="animate-spin h-4 w-4 mr-2 border-2 border-blue-500 rounded-full border-t-transparent"></div>
                            <span className="text-sm text-gray-500">
                              {t('pages.createTicket.fields.loadingTemplates')}
                            </span>
                          </div>
                        )}
                      </div>

                      {selectedTemplate && (
                        <div className="mt-4">
                          <div
                            className={`rounded-lg shadow-md p-4 ${isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}
                          >
                            <p className="mb-4">{selectedTemplate.description}</p>
                            <DynamicFormRenderer
                              formTemplate={selectedTemplate}
                              initialValues={formResponse}
                              onSubmit={(values) => setFormResponse(values)}
                              readOnly={false}
                              noForm={true} // Prevent form nesting
                            />
                          </div>
                        </div>
                      )}

                      {!selectedTemplate && (
                        <div className="text-center p-6 border border-dashed rounded-md">
                          <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Select a form template to display structured fields
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Attachments */}
              <div className="border-t pt-4">
                <label
                  className={`block text-sm font-medium ${
                    isDark ? 'text-gray-200' : 'text-gray-700'
                  } mb-2`}
                >
                  {t('forms.attachments')}
                </label>
                <FileUpload
                  onChange={(files) => {
                    setValue('attachments', files);
                    setUploadProgress({});
                  }}
                  value={watch('attachments')}
                />

                {/* Image previews for attachments */}
                {(watch('attachments') || []).length > 0 && (
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {(watch('attachments') || []).map((file, index) => (
                      <FilePreview
                        key={`${file.name}-${index}`}
                        file={file}
                        onRemove={() => {
                          const currentFiles = watch('attachments') || [];
                          const newFiles = [...currentFiles];
                          newFiles.splice(index, 1);
                          setValue('attachments', newFiles);
                        }}
                      />
                    ))}
                  </div>
                )}

                {/* Upload progress indicators */}
                {Object.entries(uploadProgress).map(([fileName, progress]) => (
                  <div key={fileName} className="mt-2">
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>{fileName}</span>
                      <span>{progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </StepFormSection>

            {/* Additional Options */}
            <div
              className={`${isDark ? 'bg-gray-700/50' : 'bg-gray-50'} border border-border rounded-lg p-4 m-4`}
            >
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('isPublic')}
                  className={`h-4 w-4 focus:ring-blue-500 rounded ${
                    isDark ? 'text-blue-500 border-gray-600' : 'text-blue-600 border-gray-300'
                  }`}
                />
                <label
                  htmlFor="isPublic"
                  className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-900'}`}
                >
                  {t('pages.createTicket.fields.notifyCustomer')}
                </label>
              </div>
              <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {t('pages.createTicket.fields.notifyCustomerDescription')}
              </p>
            </div>
          </div>
          {/* Form Actions */}
          <div className="flex justify-end space-x-4 p-4 pb-6">
            <button
              type="button"
              onClick={onCancel}
              className={`px-4 py-2 text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                isDark
                  ? 'text-gray-200 bg-gray-700 border-gray-600 hover:bg-gray-600'
                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
              }`}
              disabled={isSubmitting}
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className={`inline-flex items-center px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                isDark ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600 hover:bg-blue-700'
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {t('common.creating')}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {t('pages.createTicket.createButton')}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
      {isCustomerModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div
              className="fixed inset-0 bg-black/30"
              onClick={() => {
                setIsCustomerModalOpen(false);
                setPhoneValidationError(null);
              }}
            />
            <div
              className={`relative w-full max-w-md p-6 rounded-lg shadow-xl ${
                isDark ? 'bg-gray-800' : 'bg-white'
              }`}
            >
              <h3 className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('customers.createNew')}
              </h3>
              <form onSubmit={handleCreateCustomer} className="space-y-4">
                <div>
                  <label
                    className={`block text-sm font-medium ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    {t('customers.phoneNumber')}
                  </label>
                  <input
                    type="text"
                    value={searchPhoneNumber}
                    disabled
                    className={`mt-1 block w-full px-3 py-2 border rounded-md ${
                      phoneValidationError
                        ? isDark
                          ? 'bg-gray-700 border-red-500 text-gray-400'
                          : 'bg-gray-100 border-red-500 text-gray-500'
                        : isDark
                          ? 'bg-gray-700 border-gray-600 text-gray-400'
                          : 'bg-gray-100 border-gray-300 text-gray-500'
                    }`}
                  />
                  {phoneValidationError && (
                    <p className="mt-1 text-sm text-red-600">{phoneValidationError}</p>
                  )}
                </div>
                <div>
                  <label
                    className={`block text-sm font-medium ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    {t('customers.name')}
                  </label>
                  <div className="relative">
                    <input
                      ref={customerNameInputRef}
                      type="text"
                      value={newCustomerName}
                      onChange={(e) => setNewCustomerName(e.target.value)}
                      className={`mt-1 block w-full px-3 py-2 pr-10 border rounded-md ${
                        isDark
                          ? 'bg-gray-700 border-gray-600 text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500'
                          : 'bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500'
                      }`}
                      placeholder={t('customers.enterName')}
                      required
                    />
                    {externalRefConfig?.enabled && searchPhoneNumber && (
                      <button
                        type="button"
                        onClick={handleExternalReferenceSearch}
                        disabled={isLoadingExternalRef}
                        className={`absolute inset-y-0 right-0 flex items-center pr-3 ${
                          isLoadingExternalRef ? 'cursor-not-allowed' : 'cursor-pointer'
                        }`}
                        title={t(
                          'settings.integrationSettings.externalReference.searchTitle',
                          'Search external reference'
                        )}
                      >
                        {isLoadingExternalRef ? (
                          <ArrowPathIcon className="w-5 h-5 text-gray-400 animate-spin" />
                        ) : (
                          <MagnifyingGlassIcon
                            className={`w-5 h-5 ${
                              isDark
                                ? 'text-gray-400 hover:text-gray-300'
                                : 'text-gray-500 hover:text-gray-700'
                            }`}
                          />
                        )}
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <label
                    className={`block text-sm font-medium ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    {t('customers.externalReference')}
                  </label>
                  <input
                    type="text"
                    value={externalRef}
                    onChange={(e) => setExternalRef(e.target.value)}
                    className={`mt-1 block w-full px-3 py-2 border rounded-md ${
                      isDark
                        ? 'bg-gray-700 border-gray-600 text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500'
                        : 'bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500'
                    }`}
                    placeholder={t('customers.enterExternalReference')}
                  />
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setIsCustomerModalOpen(false);
                      setPhoneValidationError(null);
                    }}
                    className={`px-4 py-2 rounded-md text-sm font-medium ${
                      isDark
                        ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={isCreatingCustomer || !newCustomerName}
                    className={`px-4 py-2 rounded-md text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700
                      ${isCreatingCustomer || !newCustomerName ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                  >
                    {isCreatingCustomer ? t('common.creating') : t('common.create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateTicketForm;
